/**
 * Turning `file.upload` into a sentence.
 *
 * The audit log is the one screen a non-technical person is most likely to be
 * asked to read — usually because something has gone wrong and somebody wants
 * to know who did it. A column of dotted action codes answers that question
 * only for the person who wrote them.
 *
 * So each action gets a verb phrase, a category for grouping and filtering,
 * and a tone. Anything unrecognised falls back to a readable rendering of the
 * code itself rather than being hidden: a new action nobody has written a
 * phrase for yet must still appear in the log.
 */

export type AuditTone = 'neutral' | 'positive' | 'warning' | 'danger';

export interface Phrasing {
  /** Completes "Rehan ___". */
  verb: string;
  category: string;
  tone: AuditTone;
  /** One line on why this event matters, for the expanded row. */
  meaning?: string;
}

const PHRASES: Record<string, Phrasing> = {
  // Signing in and out
  'auth.login': { verb: 'signed in', category: 'Access', tone: 'neutral' },
  'auth.login.failed': {
    verb: 'failed to sign in',
    category: 'Access',
    tone: 'warning',
    meaning: 'The email existed but the password was wrong. Repeated failures lock the account.',
  },
  'auth.login.blocked': {
    verb: 'was blocked from signing in',
    category: 'Access',
    tone: 'danger',
    meaning: 'The account was locked or deactivated at the time of the attempt.',
  },
  'auth.login.totp_required': {
    verb: 'was asked for a two-factor code',
    category: 'Access',
    tone: 'neutral',
  },
  'auth.logout': { verb: 'signed out', category: 'Access', tone: 'neutral' },
  'auth.password.changed': {
    verb: 'changed their password',
    category: 'Access',
    tone: 'positive',
    meaning: 'Every session was ended, on every device.',
  },
  'auth.totp.disabled': {
    verb: 'turned off two-factor authentication',
    category: 'Access',
    tone: 'warning',
    meaning: 'The account is now protected by its password alone.',
  },

  // Accounts
  'user.create': { verb: 'created an account for', category: 'People', tone: 'positive' },
  'user.update': { verb: 'changed an account', category: 'People', tone: 'neutral' },
  'user.activate': { verb: 'reactivated an account', category: 'People', tone: 'positive' },
  'user.deactivate': {
    verb: 'deactivated an account',
    category: 'People',
    tone: 'warning',
    meaning: 'That person was signed out everywhere and can no longer sign in.',
  },
  'user.delete': { verb: 'deleted an account', category: 'People', tone: 'danger' },
  'user.reset_password': {
    verb: 'reset someone’s password',
    category: 'People',
    tone: 'warning',
  },
  'user.revoke_sessions': {
    verb: 'signed someone out everywhere',
    category: 'People',
    tone: 'warning',
  },

  // Your own account
  'profile.update': { verb: 'updated their profile', category: 'Profile', tone: 'neutral' },
  'profile.avatar.set': { verb: 'changed their picture', category: 'Profile', tone: 'neutral' },
  'profile.avatar.clear': { verb: 'removed their picture', category: 'Profile', tone: 'neutral' },
  'branding.logo.set': { verb: 'changed the system logo', category: 'Settings', tone: 'neutral' },
  'branding.logo.clear': {
    verb: 'restored the default logo',
    category: 'Settings',
    tone: 'neutral',
  },

  // Documents
  'file.upload': { verb: 'uploaded a file', category: 'Documents', tone: 'neutral' },
  'file.rename': { verb: 'renamed a file', category: 'Documents', tone: 'neutral' },
  'file.move': { verb: 'moved a file', category: 'Documents', tone: 'neutral' },
  'file.trash': {
    verb: 'moved a file to the trash',
    category: 'Documents',
    tone: 'warning',
    meaning: 'Nothing was destroyed — trashed items can be restored.',
  },
  'file.restore': { verb: 'restored a file', category: 'Documents', tone: 'positive' },
  'file.download': { verb: 'downloaded a file', category: 'Documents', tone: 'neutral' },
  'folder.create': { verb: 'created a folder', category: 'Documents', tone: 'neutral' },
  'folder.rename': { verb: 'renamed a folder', category: 'Documents', tone: 'neutral' },
  'folder.move': { verb: 'moved a folder', category: 'Documents', tone: 'neutral' },
  'folder.trash': { verb: 'moved a folder to the trash', category: 'Documents', tone: 'warning' },
  'folder.restore': { verb: 'restored a folder', category: 'Documents', tone: 'positive' },

  // Sheets
  'spreadsheet.create': { verb: 'created a sheet', category: 'Sheets', tone: 'neutral' },
  'spreadsheet.update': { verb: 'edited a sheet', category: 'Sheets', tone: 'neutral' },
  'spreadsheet.rename': { verb: 'renamed a sheet', category: 'Sheets', tone: 'neutral' },
  'spreadsheet.delete': { verb: 'deleted a sheet', category: 'Sheets', tone: 'danger' },
  'spreadsheet.import': { verb: 'imported a workbook', category: 'Sheets', tone: 'neutral' },
  'spreadsheet.export': { verb: 'exported a sheet', category: 'Sheets', tone: 'neutral' },

  // The books
  'ledger.entry.create': { verb: 'recorded a transaction', category: 'Books', tone: 'neutral' },
  'ledger.entry.update': {
    verb: 'changed a transaction',
    category: 'Books',
    tone: 'warning',
    meaning: 'The before and after values are recorded below.',
  },
  'ledger.entry.delete': { verb: 'deleted a transaction', category: 'Books', tone: 'danger' },
  'ledger.import': { verb: 'imported transactions', category: 'Books', tone: 'neutral' },
  'party.create': { verb: 'added a party', category: 'Books', tone: 'neutral' },
  'party.update': { verb: 'changed a party', category: 'Books', tone: 'neutral' },
  'account.create': { verb: 'added a ledger account', category: 'Books', tone: 'neutral' },
  'account.update': { verb: 'changed a ledger account', category: 'Books', tone: 'neutral' },

  // Invoices
  'invoice.create': { verb: 'drafted an invoice', category: 'Invoices', tone: 'neutral' },
  'invoice.update': { verb: 'edited an invoice', category: 'Invoices', tone: 'neutral' },
  'invoice.issue': {
    verb: 'issued an invoice',
    category: 'Invoices',
    tone: 'positive',
    meaning: 'Once issued, an invoice can no longer be edited — only credited.',
  },
  'invoice.void': { verb: 'voided an invoice', category: 'Invoices', tone: 'danger' },
  'invoice.delete': { verb: 'deleted a draft invoice', category: 'Invoices', tone: 'danger' },
  'invoice.payment.record': { verb: 'recorded a payment', category: 'Invoices', tone: 'positive' },
  'invoice.payment.delete': { verb: 'removed a payment', category: 'Invoices', tone: 'warning' },
};

export function phraseFor(action: string): Phrasing {
  const known = PHRASES[action];
  if (known) return known;

  // An action nobody has phrased yet. `ledger.entry.create` becomes
  // "ledger entry create" — ugly, but present and legible, which beats a row
  // that quietly does not appear.
  return {
    verb: action.replace(/[._]/g, ' '),
    category: categoryOf(action),
    tone: /delete|void|revoke|blocked/.test(action)
      ? 'danger'
      : /fail|deactivate|trash|disable/.test(action)
        ? 'warning'
        : 'neutral',
  };
}

function categoryOf(action: string): string {
  const prefix = action.split('.')[0] ?? '';
  const map: Record<string, string> = {
    auth: 'Access',
    user: 'People',
    profile: 'Profile',
    branding: 'Settings',
    file: 'Documents',
    folder: 'Documents',
    spreadsheet: 'Sheets',
  };
  return map[prefix] ?? 'Other';
}

/** Every category present, in the order they should be offered as filters. */
export const CATEGORY_ORDER = [
  'Access',
  'People',
  'Documents',
  'Sheets',
  'Books',
  'Invoices',
  'Profile',
  'Settings',
  'Other',
];

/** The action prefixes a category covers, for server-side filtering. */
export function prefixesFor(category: string): string[] {
  const map: Record<string, string[]> = {
    Access: ['auth'],
    People: ['user'],
    Documents: ['file', 'folder'],
    Sheets: ['spreadsheet'],
    Books: ['ledger', 'party', 'account'],
    Invoices: ['invoice'],
    Profile: ['profile'],
    Settings: ['branding'],
  };
  return map[category] ?? [];
}

/**
 * Render a `changes` blob as readable pairs.
 *
 * Keys arrive as camelCase field names straight from the service layer, which
 * is fine for a developer and opaque to everybody else.
 */
export function describeChanges(changes: Record<string, unknown> | null): {
  label: string;
  value: string;
}[] {
  if (!changes || typeof changes !== 'object') return [];

  return Object.entries(changes).map(([key, value]) => ({
    label: humanise(key),
    value: renderValue(value),
  }));
}

function humanise(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._]/g, ' ')
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
